// ============================================================
// HELPLESS - a target that cannot defend itself at all.
//
// Unconscious, Paralysed, Dying, or marked Helpless by hand (`Hpl`: bound,
// asleep, held down - and the GM's mark on a sentry taken from behind).
// Against one:
//
//   - a melee attack cannot miss. The d100 is still rolled - the weapon can
//     still jam or overheat - but a failed roll lands as one bare hit;
//   - damage is rolled twice and added;
//   - there is no Dodge and no Parry.
//
// Melee only, as written; a table may extend it to ranged attacks
// (`helplessRanged`). A horde is never helpless: it is a crowd, and some
// of it is always awake.
//
// "Automatically hit" and "roll damage twice" are from the Deathwatch core
// rules as recalled, not checked against the book - see
// dev-docs/decisions-log.md, 2026-10-05.
//
// Pure. Zero React dependency.
// ============================================================
import { condType } from './conditions';

const HELPLESS_CODE = 'Hpl';

/** The conditions that leave somebody helpless. Dead is not among them: there is nobody left to hit. */
const HELPLESS_CONDITIONS: readonly string[] = ['U', 'Pa', 'Dy', HELPLESS_CODE];

interface HelplessTarget {
  conditions?: readonly string[] | null;
  horde?: { isHorde?: boolean } | null;
  bodyType?: string;
}

/** Can this target do nothing to defend itself? */
export function isHelpless(target: HelplessTarget | null | undefined): boolean {
  if (!target || target.horde?.isHorde || target.bodyType === 'horde') return false;
  return !!target.conditions?.some((c) => HELPLESS_CONDITIONS.includes(condType(c)));
}

/** The table's ruling on reach: melee only unless it says ranged attacks count too. */
export interface HelplessRuling {
  helplessRanged?: boolean;
}

/** Does THIS attack get the helpless rules against this target? */
export function helplessAgainst(
  target: HelplessTarget | null | undefined,
  isMelee: boolean,
  ruling: HelplessRuling | null | undefined = null,
): boolean {
  return (isMelee || ruling?.helplessRanged === true) && isHelpless(target);
}
